import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  signal,
  ViewChild,
  OnInit,
  OnDestroy,
} from '@angular/core';
import { FieldType, FormlyModule } from '@ngx-formly/core';
import { MatInputModule } from '@angular/material/input';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import {
  MatChip,
  MatChipEditedEvent,
  MatChipGrid,
  MatChipInput,
  MatChipInputEvent,
  MatChipRemove,
  MatChipRow,
  MatChipSet,
} from '@angular/material/chips';
import { MatIcon } from '@angular/material/icon';
import { MatAutocomplete, MatAutocompleteTrigger, MatOption } from '@angular/material/autocomplete';
import { MatMenuModule } from '@angular/material/menu';
import { debounceTime, distinctUntilChanged, isObservable, startWith, take, takeUntil } from 'rxjs';
import { injectDestroy } from 'ngxtension/inject-destroy';
import { DynamicOptionsService } from './services/dynamic-options.service';
import { cloneDeep } from 'lodash-es';
import { InfoTooltipComponent } from '../info-tooltip.component';
import { FieldLabelRowComponent } from './field-label-row.component';
import { TippyTooltipDirective } from '../../directives/tippy-tooltip.directive';
import { FormlyErrorComponent } from '../formly-error.component';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { FormlyDataCyDirective } from '../../directives/formly-data-cy.directive';
import { LinkButtonComponent } from '../link-button.component';
import { MatMenuTrigger } from '@angular/material/menu';
import type { TagsMenuOption } from './tags.types';
import { nestOptionsByGroup } from './tags-group-nesting.util';
import { resolveManualOptionLabel, resolveManualOptionValue } from './manual-option.util';

@Component({
  selector: 'formly-mat-tags',
  template: `
    <div [class.hidden]="isFieldHidden()">
      @if (isButtonsMode()) {
        <!--
          Figma "Interested in" / "Licences" (node 3281:135265): the options sit on the card as
          buttons, each prefixed with a plus while unselected. No chip row — the buttons carry the
          state themselves, so selecting is one click instead of open-overlay-pick-close.
        -->
        <div class="dd-tags-buttons-field w-full">
          <!-- Gedeelde labelrij i.p.v. een eigen span: die levert ook het Figma-badge
               "Optioneel" voor een niet-verplicht veld, rechts op de veldrand (#20952). -->
          <span class="dd-tags-buttons-field__label">
            <app-field-label-row
              [label]="props.label + (field.props.required ? ' *' : '')"
              [required]="field.props.required"
              [info]="props.info"></app-field-label-row>
          </span>
          <div
            class="dd-tags-buttons"
            role="group"
            [attr.aria-label]="props.label + (field.props.required ? ' (verplicht)' : '')"
            [formlyDataCy]="field">
            @for (option of availableOptions(); track option.value ?? option.label) {
              <!-- Computed once per button: without these @lets, capped (isOptionCapped) and
                   selected (isValueSelected) would each be recomputed by every one of their
                   three template reads below, per change-detection pass (finding 3,
                   #20951/#20952 review). -->
              @let capped = isOptionCapped(option);
              @let selected = isValueSelected(option.value);
              <button
                type="button"
                class="dd-tags-option-button"
                [class.dd-tags-option-button--selected]="selected"
                [class.dd-tags-option-button--capped]="capped"
                [disabled]="!isInteractive()"
                [attr.aria-pressed]="selected"
                [attr.aria-disabled]="capped ? 'true' : null"
                [tippyTooltip]="maxTagsTooltip()"
                [tippyTooltipDisabled]="!capped"
                (click)="selectMenuOption(option, $event)">
                <mat-icon
                  class="dd-tags-option-button__icon"
                  [svgIcon]="selected ? 'feather:check' : 'heroicons_outline:plus'"></mat-icon>
                {{ option.label }}
              </button>
            }
            @if (isLoading()) {
              <mat-spinner diameter="18"></mat-spinner>
            }
          </div>
          <!--
            Values that are not in the option list (free text typed before the field moved to
            buttons, or an option that has since been removed at the source) would otherwise be
            invisible AND unremovable. They keep the normal chip so they can still be taken off.
          -->
          @if (orphanTags().length) {
            <mat-chip-set class="dd-tags-menu-field__chips" aria-label="Overige tags">
              @for (tag of orphanTags(); track $index) {
                <mat-chip
                  [disabled]="isDisabled()"
                  class="dd-tag-chip"
                  [class.dd-tag-chip--removable]="isInteractive()"
                  [attr.aria-label]="isInteractive() ? 'Verwijder ' + tagLabel(tag) : null"
                  [attr.role]="isInteractive() ? 'button' : null"
                  [attr.tabindex]="isInteractive() ? 0 : null"
                  (removed)="remove(tag)"
                  (click)="isInteractive() && remove(tag)"
                  (keydown.enter)="isInteractive() && remove(tag)"
                  (keydown.space)="isInteractive() && remove(tag); $event.preventDefault()">
                  @if (isInteractive()) {
                    <mat-icon class="dd-tag-chip__state dd-tag-chip__state--rest" svgIcon="feather:check"></mat-icon>
                    <mat-icon class="dd-tag-chip__state dd-tag-chip__state--hover" svgIcon="feather:minus"></mat-icon>
                  }
                  <span class="dd-tag-chip__label">{{ tagLabel(tag) }}</span>
                </mat-chip>
              }
            </mat-chip-set>
          }
          <app-formly-error [field]="field"></app-formly-error>
        </div>
      } @else if (isMenuMode()) {
        <div class="dd-tags-menu-field w-full">
          <!-- Figma: the add-button names the subject ("Add feature"), so a separate
               label above it is redundant. The label renders only when the button is
               absent (read-only/disabled) — without it the chips would lose their context.

               tagsShowLabel opts back in: the company-settings Fleet-preferences design
               (node 3266:135261) pairs a grey label with a SHORTER button ("Locations
               (Regional & International)" above "Add location"), so the two are not
               duplicates there. Combine it with addText to keep the button short. -->
          @if (!isInteractive() || props.tagsShowLabel) {
            <span class="dd-tags-menu-field__label">
              <!-- Shared label row rather than a private copy: it is what renders the Figma
                   "Optioneel" badge for a non-required field, so this presentation matches the
                   ~20 other field types that already use it (#20952). The asterisk stays inside
                   the label text — this block is boxless, so there is no Material required
                   marker to defer to. -->
              <app-field-label-row
                [label]="(props.label ?? '') + (field.props.required ? ' *' : '')"
                [required]="field.props.required"
                [info]="props.info"></app-field-label-row>
            </span>
          }
          <mat-chip-set class="dd-tags-menu-field__chips" aria-label="Tags">
            <!-- Figma: the add-affordance sits LEFT of the chips and stays visible
                 whether or not values are present — but not for read-only/disabled
                 viewers, for whom the field is a plain value display. -->
            @if (isInteractive()) {
              <button
                type="button"
                class="dd-tags-add-button"
                [formlyDataCy]="field"
                [matMenuTriggerFor]="tagsMenu"
                (menuOpened)="onMenuOpened()">
                <mat-icon class="icon-size-5" svgIcon="heroicons_outline:plus"></mat-icon>
                {{ addLabel() }}
              </button>
              <!-- Conditional: an empty tooltip host is still a flex item, doubling the
                   12px gap between the button and the first chip. -->
              @if (props.info) {
                <app-info-tooltip [tooltip]="props.info"></app-info-tooltip>
              }
            }
            <!-- Figma chip states: leading check at rest, minus on hover, click removes.
                 No trailing remove slot — the whole chip is the remove control.
                 Read-only: chips keep their normal accent styling, minus the interaction. -->
            @for (tag of tags(); track $index) {
              <mat-chip
                [disabled]="isDisabled()"
                class="dd-tag-chip"
                [class.dd-tag-chip--removable]="isInteractive()"
                [attr.aria-label]="isInteractive() ? 'Verwijder ' + tagLabel(tag) : null"
                (removed)="remove(tag)"
                (click)="isInteractive() && remove(tag)">
                @if (isInteractive()) {
                  <mat-icon class="dd-tag-chip__state dd-tag-chip__state--rest" svgIcon="feather:check"></mat-icon>
                  <mat-icon class="dd-tag-chip__state dd-tag-chip__state--hover" svgIcon="feather:minus"></mat-icon>
                }
                <span class="dd-tag-chip__label">{{ tagLabel(tag) }}</span>
              </mat-chip>
            }
          </mat-chip-set>
          <!-- De --nested modifier is de HAAK VOOR NESTED-ONLY STYLING. Het geneste Figma-paneel
           (land → regio's) heeft eigen maten; die mogen niet doorwerken op de tags-velden
           elders in de app die één platte lijst tonen. Alles wat alleen bij twee niveaus
           hoort, hangt onder deze klasse — nooit op .dd-tags-menu zelf. -->
          <mat-menu
            #tagsMenu="matMenu"
            [class]="hasNestedOptions() ? 'dd-tags-menu dd-tags-menu--nested' : 'dd-tags-menu'"
            (closed)="onMenuClosed()">
            <div class="dd-tags-menu__search">
              <input
                type="text"
                class="dd-tags-menu__search-input"
                placeholder="Zoeken..."
                [formControl]="tagsInput"
                (click)="$event.stopPropagation()"
                (keydown)="$event.stopPropagation()" />
              @if (isLoading()) {
                <mat-spinner diameter="18"></mat-spinner>
              }
            </div>
            <!-- Geen mat-menu-item-directives: gewone buttons sluiten de overlay niet
                 automatisch, dus multi-select kan meerdere keuzes doen; single-select
                 sluit expliciet via menuTrigger.closeMenu(). -->
            <div class="dd-tags-menu__options">
              <!-- Geneste subview (Figma "Select region"): terug-navigatie + breadcrumb,
                   daarna "Alles in …" en de kinderen van de actieve parent. -->
              @if (menuParent(); as parent) {
                <div class="dd-tags-menu__nav">
                  <button
                    type="button"
                    dd-link-button
                    icon="heroicons_outline:arrow-left"
                    (click)="closeSubmenu($event)">
                    Terug
                  </button>
                </div>
                <div class="dd-tags-menu__breadcrumb">
                  {{ parent.label }} &gt; {{ props.nestedChildrenLabel || 'Opties' }}
                </div>
                <!-- Computed once, same reason as the capped @lets below: one evaluation per
                     change-detection cycle instead of three separate bindings each calling it. -->
                @let allCapped = isBulkSelectCapped(parent);
                <button
                  type="button"
                  class="dd-tags-menu__option"
                  [class.dd-tags-menu__option--selected]="isParentFullySelected(parent)"
                  [class.dd-tags-menu__option--capped]="allCapped"
                  [attr.aria-pressed]="isParentFullySelected(parent)"
                  [attr.aria-disabled]="allCapped ? 'true' : null"
                  [tippyTooltip]="maxTagsTooltip()"
                  [tippyTooltipDisabled]="!allCapped"
                  (click)="selectAllInParent(parent, $event)">
                  @if (isParentFullySelected(parent)) {
                    <mat-icon class="dd-tags-menu__option-check" svgIcon="feather:check"></mat-icon>
                  }
                  <span class="dd-tags-menu__option-label">Alles in {{ parent.label }}</span>
                </button>
                @for (child of visibleChildren(); track child.value ?? child.label) {
                  @let capped = isOptionCapped(child);
                  <button
                    type="button"
                    class="dd-tags-menu__option"
                    [class.dd-tags-menu__option--selected]="isValueSelected(child.value)"
                    [class.dd-tags-menu__option--capped]="capped"
                    [attr.aria-disabled]="capped ? 'true' : null"
                    [tippyTooltip]="maxTagsTooltip()"
                    [tippyTooltipDisabled]="!capped"
                    (click)="selectMenuOption(child, $event)">
                    @if (isValueSelected(child.value)) {
                      <mat-icon class="dd-tags-menu__option-check" svgIcon="feather:check"></mat-icon>
                    }
                    <span class="dd-tags-menu__option-label">{{ child.label }}</span>
                  </button>
                }
                @if (visibleChildren().length === 0) {
                  <div class="dd-tags-menu__empty">Geen resultaten</div>
                }
              } @else {
                @if (visibleOptions().length > 0) {
                  @for (option of visibleOptions(); track option.value ?? option.label) {
                    @if (hasChildren(option)) {
                      <!-- Parent-item (Figma type=Country): chevron rechts, count-pill bij selectie. -->
                      <button type="button" class="dd-tags-menu__option" (click)="openSubmenu(option, $event)">
                        <span class="dd-tags-menu__option-label">{{ option.label }}</span>
                        @if (selectedChildCount(option) > 0) {
                          <span class="dd-tags-menu__counter">{{ selectedChildCount(option) }}</span>
                        }
                        <mat-icon class="dd-tags-menu__option-chevron" svgIcon="feather:chevron-right"></mat-icon>
                      </button>
                    } @else {
                      @let capped = isOptionCapped(option);
                      <button
                        type="button"
                        class="dd-tags-menu__option"
                        [class.dd-tags-menu__option--selected]="isValueSelected(option.value)"
                        [class.dd-tags-menu__option--capped]="capped"
                        [attr.aria-disabled]="capped ? 'true' : null"
                        [tippyTooltip]="maxTagsTooltip()"
                        [tippyTooltipDisabled]="!capped"
                        (click)="selectMenuOption(option, $event)">
                        @if (isValueSelected(option.value)) {
                          <mat-icon class="dd-tags-menu__option-check" svgIcon="feather:check"></mat-icon>
                        }
                        <span class="dd-tags-menu__option-label">{{ option.label }}</span>
                      </button>
                    }
                  }
                } @else if (!isLoading() && !freeTextCandidate()) {
                  <div class="dd-tags-menu__empty">Geen resultaten</div>
                }
                <!-- Free text stays available in menu mode for requireSelection === false
                     fields (the pre-menu inline input accepted it); without this row those
                     fields would lose values that aren't in the option list. -->
                @if (freeTextCandidate(); as term) {
                  <!-- Free text is never an already-selected value, so its capped state is just
                       the shared max check — no isValueSelected() branch needed here, unlike
                       isOptionCapped(). -->
                  @let freeTextCapped = isInteractive() && isMaxTagsReached();
                  <button
                    type="button"
                    class="dd-tags-menu__option"
                    [class.dd-tags-menu__option--capped]="freeTextCapped"
                    [attr.aria-disabled]="freeTextCapped ? 'true' : null"
                    [tippyTooltip]="maxTagsTooltip()"
                    [tippyTooltipDisabled]="!freeTextCapped"
                    (click)="addFreeText($event)">
                    <mat-icon class="dd-tags-menu__option-check" svgIcon="feather:plus"></mat-icon>
                    <span class="dd-tags-menu__option-label">'{{ term }}' toevoegen</span>
                  </button>
                }
              }
            </div>
          </mat-menu>
          @if (field.props.description) {
            <span class="dd-tags-menu-field__hint">{{ field.props.description }}</span>
          }
          <app-formly-error [field]="field"></app-formly-error>
        </div>
      } @else {
        <mat-form-field
          class="tags-list w-full"
          appearance="outline"
          floatLabel="always"
          [class.mat-form-field-invalid]="formControl.invalid && formControl.touched">
          <mat-label>
            <!-- Same shared row as every other mat-form-field field type (linkage-tags, number,
                 autocomplete, …): the .labels-above rules in _forms.scss right-align the
                 "Optioneel" badge on the field edge once the row is here (#20952).
                 Do NOT drop the literal '*' as redundant: it is the ONLY required indicator this
                 field has. Material's own marker is gated on its form-field control reporting
                 required, and that control is the mat-chip-grid below — which carries neither
                 [required] nor a form control (both sit on the <input> inside it), so it reports
                 false and no marker is rendered. -->
            <app-field-label-row
              [label]="(props.label ?? '') + (field.props.required ? ' *' : '')"
              [required]="field.props.required"
              [info]="props.info"></app-field-label-row>
          </mat-label>
          <!-- Chips INSIDE the grid: outside it, the grid (with the input) becomes its own
               block and the caret jumps to the next line even when the current one has room. -->
          <mat-chip-grid #chipGrid aria-label="Tags invoerveld">
            @for (tag of tags(); track $index) {
              <mat-chip-row
                [disabled]="isDisabled()"
                class="mr-1 dd-tag-chip"
                (removed)="remove(tag)"
                [editable]="true"
                (edited)="edit(tag, $event)"
                [aria-description]="'press enter to edit ' + tag">
                <span class="dd-tag-chip__label">{{ tagLabel(tag) }}</span>
                @if (!isDisabled()) {
                  <!-- matChipRemove belongs on the button only. It was also on the icon, which
                       made MatChipRemove give the icon role="button" INSIDE the button — axe
                       flagged nested-interactive plus an unnamed command, and a screen reader
                       announced two remove controls per tag. The icon is decoration here; the
                       button already carries the label. -->
                  <button matChipRemove class="dd-tag-remove" [attr.aria-label]="'remove ' + tag">
                    <mat-icon aria-hidden="true" [svgIcon]="'close'"></mat-icon>
                  </button>
                }
              </mat-chip-row>
            }
            <input
              matInput
              type="text"
              class="pl-2"
              #input
              [required]="props.required"
              [formControl]="tagsInput"
              [formlyDataCy]="field"
              [placeholder]="isDisabled() ? '' : placeholder()"
              [matChipInputFor]="chipGrid"
              [matChipInputSeparatorKeyCodes]="separatorKeyCodes"
              (matChipInputTokenEnd)="add($event)"
              (focus)="onInputFocus()"
              [matAutocomplete]="auto" />
            @if (isLoading()) {
              <mat-spinner class="absolute right-8" diameter="24"> </mat-spinner>
            }
            <mat-autocomplete #auto="matAutocomplete" (optionSelected)="selected($event)">
              @for (option of visibleOptions(); track option.value || option.label) {
                <mat-option [value]="option.value">{{ option.label }}</mat-option>
              }
            </mat-autocomplete>
          </mat-chip-grid>
          @if (field.props.description) {
            <mat-hint class="inline-block mb-2">{{ field.props.description }}</mat-hint>
          }
        </mat-form-field>
        <app-formly-error [field]="field"></app-formly-error>
      }
    </div>
  `,
  styles: `
    /* Remove button on a chip: hidden by default, revealed on hover or while it (or the chip
       itself) holds keyboard focus. Uses opacity + pointer-events rather than visibility/display
       so the button stays in the tab order and is reachable by keyboard — the same mechanism
       fixed for the clear-field button (#20809: "Wisknop blijft toetsenbord-bereikbaar"), applied
       here to the opposite default (hidden until hover/focus instead of hidden only while typing). */
    .dd-tag-remove {
      opacity: 0;
      pointer-events: none;
      transform: scale(0.85);
      transition:
        opacity 120ms ease-out,
        transform 120ms ease-out;
    }

    mat-chip-row:hover .dd-tag-remove,
    mat-chip:hover .dd-tag-remove,
    mat-chip-row:focus-within .dd-tag-remove,
    mat-chip:focus-within .dd-tag-remove {
      opacity: 1;
      pointer-events: auto;
      transform: scale(1);
    }

    /* Touch/coarse-pointer users have no reliable hover state — keep the remove affordance
       visible immediately, matching the convention used for the select-field clear button. */
    @media (hover: none), (pointer: coarse) {
      .dd-tag-remove {
        opacity: 1;
        pointer-events: auto;
        transform: scale(1);
      }
    }

    .dd-tags-menu-field {
      display: flex;
      flex-direction: column;
      gap: 8px;
      /* Veldritme: gewone velden dragen hieronder de subscript-strook van hun
         mat-form-field; deze boxless weergave heeft die niet, waardoor twee
         gestapelde tags-velden ("Add feature" boven "Add skill") tegen elkaar
         plakten. Figma's Features & skills-sectie stapelt met een 20px gap. */
      margin-bottom: 20px;
    }

    /* Typography stays exactly as it is — restoring the label row is not a licence to restyle
       it. font-size/weight/line-height/color are inherited properties, so they carry through to
       .dd-field-label-text inside the row; the "Optioneel" badge sets its own 12px in
       _forms.scss. display:block makes the full width explicit so the badge lands on the right
       edge: this span is already a stretched flex item of .dd-tags-menu-field, so this is
       parity with .dd-tags-buttons-field__label below rather than a fix. */
    .dd-tags-menu-field__label {
      display: block;
      font-size: 14px;
      font-weight: 500;
      line-height: 1.3;
      color: var(--dd-accent, #545559);
    }

    /* Boxless (Figma): add-button and chips stand loose on the card — no border,
       no field background around the group. 12px gap per the interaction examples. */
    .dd-tags-menu-field__chips {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      column-gap: 12px;
      row-gap: 8px;
      min-height: 32px;
    }

    /* mat-chip-set wraps the chips in its own flex row with default margins; flatten
       it so the 32px chips centre on the same line as the 32px add-button instead of
       riding on the set's internal spacing. */
    .dd-tags-menu-field__chips ::ng-deep .mdc-evolution-chip-set__chips {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      column-gap: 12px;
      row-gap: 8px;
      margin: 0;
    }

    .dd-tags-menu-field__chips ::ng-deep .mat-mdc-chip {
      margin: 0;
    }

    /* Buttons mode. Same column rhythm as the menu mode next to it, so a card that mixes the two
       (Fleet preferences: Market type on a button, Interested in on option buttons) keeps one
       baseline grid. */
    .dd-tags-buttons-field {
      display: flex;
      flex-direction: column;
      gap: 8px;
      margin-bottom: 20px;
    }

    /* Zelfde labelstijl als elk ander veld in hetzelfde formulier (Figma 14px Medium,
       Foreground/Default/Primary); de labelrij zelf spant de veldbreedte zodat het
       "Optioneel"-badge rechts landt. */
    .dd-tags-buttons-field__label {
      display: block;
      font-size: 0.875rem;
      font-weight: 500;
      line-height: 1.4;
      color: var(--dd-field-label);
    }

    .dd-tags-buttons {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      column-gap: 12px;
      row-gap: 8px;
      min-height: 32px;
    }

    .dd-tags-menu-field__hint {
      font-size: 0.75rem;
      color: var(--dd-field-placeholder);
    }

    /* Add-button styling lives in styles/components/_chips.scss, next to the chip:
       both share the %dd-chip-row-anatomy base so their geometry cannot drift. */

    ::ng-deep .dd-tags-menu {
      min-width: 240px;
      max-width: 320px;
      /* Figma "Menu open" (node 2867:127786): 6px between the add-button and the panel. */
      margin-top: 6px;
    }

    ::ng-deep .dd-tags-menu .mat-mdc-menu-content {
      padding: 0;
    }

    .dd-tags-menu__search {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      padding: 0.5rem 0.75rem;
      border-bottom: 1px solid var(--fuse-border);
    }

    .dd-tags-menu__search-input {
      flex: 1 1 auto;
      min-width: 0;
      border: 0;
      outline: none;
      background: transparent;
      font-size: 0.875rem;
      color: var(--dd-field-label);
    }

    .dd-tags-menu__options {
      max-height: 280px;
      overflow-y: auto;
      padding: 4px 8px 8px;
    }

    /* Menu-items volgens de Figma Menu item-set (nodes 444:18378 e.o.): 32px, pad
       6/4/6/8, gap 8, Inter Medium 14; hover = 5%-wash op radius 6; selected =
       Background/default/Accent-subtle met check en accent-tekst. */
    .dd-tags-menu__option {
      display: flex;
      align-items: center;
      gap: 8px;
      width: 100%;
      min-height: 32px;
      padding: 6px 4px 6px 8px;
      border: 0;
      border-radius: 6px;
      background: transparent;
      color: var(--dd-field-label, #000000);
      font-size: 0.875rem;
      font-weight: 500;
      line-height: 1.4;
      text-align: left;
      cursor: pointer;
    }

    .dd-tags-menu__option:hover {
      background: color-mix(in srgb, var(--dd-btn-neutral-state-layer, #000000) 5%, transparent);
    }

    .dd-tags-menu__option--selected {
      background: var(--dd-badge-accent-bg, #fbe3ff);
      color: var(--dd-badge-accent-fg, #a12db6);
    }

    .dd-tags-menu__option--selected:hover {
      background: color-mix(in srgb, var(--dd-btn-neutral-state-layer, #000000) 5%, var(--dd-badge-accent-bg, #fbe3ff));
    }

    /* maxTags reached (#20952): the row stays readable and focusable, only the click is
       refused — same "not-allowed click" contract as the buttons-mode
       .dd-tags-option-button--capped in _chips.scss, but that one is a bordered outline
       filter-chip on a light surface (cursor change alone reads clearly there), while this
       is a borderless list row inside a dropdown, so it needs its own dimmed look to stand
       out from its selectable siblings. Cursor is the first signal, the tippy tooltip
       (maxTagsTooltip()) explains it. Opacity only: scheme-neutral, no separate dark
       override needed. */
    .dd-tags-menu__option--capped {
      cursor: not-allowed;
      opacity: 0.5;
    }

    .dd-tags-menu__option--capped:hover {
      background: transparent;
    }

    .dd-tags-menu__option-label {
      flex: 1 1 auto;
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .dd-tags-menu__option-check,
    .dd-tags-menu__option-chevron {
      width: 16px;
      min-width: 16px;
      height: 16px;
      min-height: 16px;
      font-size: 16px;
      color: currentColor;
    }

    /* Count-pill (Figma Counter Type=Brand Style=Outline): wit vlak, 1px accent-rand,
       volledig rond, accent-cijfer. */
    .dd-tags-menu__counter {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      min-width: 16px;
      height: 16px;
      padding: 0 4px;
      border: 1px solid var(--fuse-primary);
      border-radius: 9999px;
      background: var(--dd-btn-primary-label, #ffffff);
      color: var(--fuse-primary);
      font-size: 10px;
      font-weight: 500;
      line-height: 1;
    }

    /* Terug-rij (Figma type=Navigation): linkknop met scheidingslijn eronder. */
    .dd-tags-menu__nav {
      padding: 4px 4px 8px;
      border-bottom: 1px solid var(--dd-field-border, #eeeff1);
      margin-bottom: 4px;
    }

    /* Breadcrumb (Figma type=Label): "United Kingdom > Regions" in secundair grijs. */
    .dd-tags-menu__breadcrumb {
      padding: 6px 8px;
      color: var(--dd-accent, #545559);
      font-size: 0.875rem;
      font-weight: 500;
      line-height: 1.4;
    }

    .dd-tags-menu__empty {
      padding: 0.75rem 1rem;
      font-size: 0.875rem;
      color: var(--dd-field-placeholder);
      text-align: center;
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatInputModule,
    ReactiveFormsModule,
    FormlyModule,
    MatChipGrid,
    MatChipRow,
    MatChip,
    MatChipSet,
    MatChipInput,
    MatIcon,
    MatChipRemove,
    MatAutocomplete,
    MatAutocompleteTrigger,
    MatOption,
    MatMenuModule,
    InfoTooltipComponent,
    FieldLabelRowComponent,
    TippyTooltipDirective,
    FormlyErrorComponent,
    LinkButtonComponent,
    MatProgressSpinner,
    FormlyDataCyDirective,
  ],
})
export class FormlyMatTagsInputComponent extends FieldType implements AfterViewInit, OnInit, OnDestroy {
  @ViewChild('input') input: ElementRef<HTMLInputElement>;
  @ViewChild(MatMenuTrigger) menuTrigger?: MatMenuTrigger;
  private dynamicOptionsService = inject(DynamicOptionsService);
  private destroy$ = injectDestroy();

  tags = signal<any[]>([]);
  availableOptions = signal<Array<{ label: string; value: any }>>([]);

  /**
   * Grow-only label lookup for values that are no longer in `availableOptions` — a search
   * fetch REPLACES the option list, but chips for already-selected values must keep their
   * label (PR 9479 review B5: an id-typed tag rendered its raw GUID during/after a search
   * until the overlay was reopened). Keyed via tagKey, same as the chip dedupe.
   */
  readonly #labelIndex = new Map<string, string>();
  linkedData = signal([]);
  private dynamicFilterValue = signal(undefined);

  // Form Control
  tagsInput = new FormControl();

  readonly separatorKeyCodes = [13, 188]; // Enter and comma

  isFieldHidden = computed(
    () => this.props.hideIfOptionsEmpty && (this.availableOptions().length === 0 || this.dynamicFilterValue() == null)
  );

  placeholder = computed(() => {
    if (!this.props.placeholder && this.props.requireSelection === true) {
      return 'Zoeken...';
    }
    return this.props.placeholder || '';
  });

  /**
   * "Toevoegen"-button + overlay is the DEFAULT presentation for fields with an option
   * source (Figma "Interaction examples - Add locations / remove": outline add-button +
   * loose chips). `tagsDisplayMode === 'inline'` is the explicit opt-out back to the
   * always-visible input with autocomplete; free-text fields without options keep the
   * inline input regardless — an add-menu without options has nothing to offer.
   */
  isMenuMode = computed(() => {
    if (this.props.tagsDisplayMode === 'menu') return true;
    if (this.props.tagsDisplayMode === 'inline') return false;
    if (this.props.tagsDisplayMode === 'buttons') return this.hasOptionSource();
    return this.hasOptionSource();
  });

  /**
   * Every option as its own toggle button instead of one add-button plus overlay (Figma
   * "Interested in" / "Licences" on the Fleet-preferences card, node 3281:135265).
   *
   * Explicitly configured rather than inferred from the option source: whether the choices fit
   * on the card is a judgement about their number and label length, and a module-backed source
   * can be just as short as a hand-written list.
   *
   * Two guards. It needs a real option source — there is nothing to lay out otherwise. And it
   * stands down for a two-level list: a country that drills down to its regions cannot be a
   * single toggle, so `locations` keeps the overlay even if someone selects this mode.
   */
  isButtonsMode = computed(
    () => this.props.tagsDisplayMode === 'buttons' && this.hasOptionSource() && !this.hasNestedOptions()
  );

  /**
   * Selected values that no option button represents: free text entered before the field moved to
   * buttons mode, or an option since removed at the source. They still render as a chip, otherwise
   * a stored value would be both invisible and impossible to remove.
   */
  orphanTags = computed(() => {
    const optionKeys = new Set((this.availableOptions() ?? []).map(option => this.tagKey(option.value)));
    return this.tags().filter(tag => !optionKeys.has(this.tagKey(tag)));
  });

  private hasOptionSource(): boolean {
    // A REAL source only: module-linked optionsArray, a non-empty static list, or an
    // observable. Free-text fields carry `options: []` and must stay on the inline input.
    if (this.field.props?.optionsArray?.length) return true;
    const options = this.field.props?.options;
    return isObservable(options) || (Array.isArray(options) && options.length > 0);
  }

  /**
   * A handwritten option list configured in the form builder ("Handmatig"), normalised to
   * the `{label, value}` shape the overlay renders.
   *
   * Without this the manual list never reached the field: `createLinkedData` turns EVERY
   * `optionsArray` entry into a module lookup regardless of `optionsType`, so a manual list
   * was fetched as if it were a module and came back empty. Stored options carry the text
   * under `option`; `label`/`value` are honoured when present so a richer config still works.
   */
  #manualOptions(): Array<{ label: string; value: unknown }> | null {
    const source = this.field.props?.optionsArray?.[0];
    if (source?.optionsType !== 'manual' || !Array.isArray(source.options)) {
      return null;
    }
    // Truthy fallback (not nullish), same as manual-option.util's own rationale: a manual option
    // saved with an empty-string label must still fall back to `option`, matching what the live
    // form renders (FormService.generateManualOptions) — a nullish-only fallback treated `''` as
    // a real label and silently dropped the option below instead of showing its fallback text.
    return source.options
      .map(option => ({
        label: resolveManualOptionLabel(option),
        value: resolveManualOptionValue(option),
      }))
      .filter(option => option.label != null && option.label !== '');
  }

  /** Actieve parent in de geneste menu-weergave (null = hoofdlijst). */
  menuParent = signal<TagsMenuOption | null>(null);

  /**
   * Selected-tag keys as a Set, shared by every "is this value selected?" check in the
   * component (isValueSelected, visibleOptions/visibleChildren filtering, the count-pill,
   * bulk-select gating). Only recomputes when `tags()` itself changes — a template read that
   * merely re-runs on an unrelated change-detection pass (e.g. `isLoading` toggling) gets the
   * cached Set back for free instead of re-walking `tags()`.
   *
   * Finding 3 (review round of #20951/#20952): buttons mode calls the per-value check up to
   * 4× per option button per CD pass (class binding, aria-pressed, icon ternary, plus once
   * inside isOptionCapped), and the old `tags().some(...)` scan recomputed a key for every tag
   * on every one of those calls — O(options × tags) work on every tick, even when nothing
   * changed. This Set turns each check into an O(1) `.has()` lookup.
   */
  readonly #selectedKeys = computed(() => new Set(this.tags().map(tag => this.tagKey(tag))));

  /**
   * Hoofdlijst van het menu. Multi-select (menu blijft open) toont geselecteerde opties
   * mét indicatie in plaats van ze te verbergen; single-select filtert ze weg (US #20806,
   * item 3). Parents (geneste opties) blijven altijd zichtbaar — hun kinderen dragen de
   * selectie. De zoekterm matcht ook op kind-labels, zodat "Bavaria" Duitsland vindt.
   */
  visibleOptions = computed(() => {
    const selectedKeys = this.#selectedKeys();
    return this.availableOptions().filter(option => {
      if (this.hasChildren(option)) return true;
      if (this.isMultiSelect()) return true;
      return !selectedKeys.has(this.tagKey(option.value));
    });
  });

  /**
   * Typed term to offer as a free-text tag in the overlay — only for requireSelection === false
   * fields (mirrors add()'s inline gate) and only when no option label matches it exactly.
   */
  freeTextCandidate = computed(() => {
    if (this.props.requireSelection !== false) return '';
    const term = (this.menuSearchTerm() ?? '').trim();
    if (!term) return '';
    const lower = term.toLowerCase();
    const matchesOption = this.availableOptions().some(
      (option: TagsMenuOption) =>
        (option.label ?? '').toLowerCase() === lower ||
        option.children?.some(child => (child.label ?? '').toLowerCase() === lower)
    );
    return matchesOption ? '' : term;
  });

  /**
   * Add the typed term as a free-text tag from the overlay (menu mode).
   *
   * Fix (#20952 review round 1): this used to add unconditionally, so a buttons-configured
   * field with `requireSelection: false` could exceed `maxTags` by typing a term instead of
   * picking one — worse than a picked option's silent refusal, since it silently ACCEPTED past
   * the limit. Same cap check as `selectMenuOption`, same CONFIGURED-mode scoping (see
   * `isMaxTagsReached`'s JSDoc).
   */
  addFreeText(event?: Event): void {
    event?.stopPropagation();
    const term = this.freeTextCandidate();
    if (!term) return;
    this.field.formControl.markAsTouched();
    if (this.isMaxTagsReached()) {
      return;
    }
    this.addTagIfAbsent(term);
    this.tagsInput.setValue(null, { emitEvent: false });
    this.menuSearchTerm.set('');
    if (!this.isMultiSelect()) this.menuTrigger?.closeMenu();
  }

  /** Kinderen van de actieve parent, client-side gefilterd op de zoekterm. */
  visibleChildren = computed(() => {
    const parent = this.menuParent();
    if (!parent?.children?.length) return [];
    const term = (this.menuSearchTerm() ?? '').toLowerCase();
    const selectedKeys = this.#selectedKeys();
    return parent.children.filter(child => {
      if (term && !(child.label ?? '').toLowerCase().includes(term)) return false;
      if (this.isMultiSelect()) return true;
      return !selectedKeys.has(this.tagKey(child.value));
    });
  });

  /** Spiegel van het zoekveld als signal, zodat computeds erop kunnen reageren. */
  menuSearchTerm = signal<string>('');

  isMultiSelect(): boolean {
    return this.props.tagsMenuMultiSelect === true;
  }

  hasChildren(option: TagsMenuOption): boolean {
    return Array.isArray(option?.children) && option.children.length > 0;
  }

  /**
   * Draagt dit veld een twee-niveau-lijst? Stuurt de `--nested` modifier op het paneel,
   * zodat nested-specifieke styling niet lekt naar de platte tags-velden elders.
   */
  hasNestedOptions = computed(() =>
    (this.availableOptions() as TagsMenuOption[]).some(option => this.hasChildren(option))
  );

  /** O(1) via the shared `#selectedKeys` Set — see its JSDoc for why (finding 3). */
  isValueSelected(value: unknown): boolean {
    return this.#selectedKeys().has(this.tagKey(value));
  }

  /**
   * Aantal geselecteerde kinderen van een parent — de count-pill (Figma "Germany (12)").
   * Telt puur de kinderen: sinds "Alles in {parent}" de kinderen zelf aanvinkt is er geen
   * parent-waarde meer die apart geteld moet worden.
   */
  selectedChildCount(option: TagsMenuOption): number {
    const selectedKeys = this.#selectedKeys();
    return (option.children ?? []).filter(child => selectedKeys.has(this.tagKey(child.value))).length;
  }

  openSubmenu(option: TagsMenuOption, event?: Event): void {
    event?.stopPropagation();
    this.menuParent.set(option);
    this.tagsInput.setValue(null, { emitEvent: false });
    this.menuSearchTerm.set('');
  }

  closeSubmenu(event: Event): void {
    event.stopPropagation();
    this.menuParent.set(null);
    this.tagsInput.setValue(null, { emitEvent: false });
    this.menuSearchTerm.set('');
  }

  /**
   * "Alles in {parent}": vinkt de KINDEREN aan, niet de parent als eigen waarde.
   *
   * De parent-als-waarde variant sloeg een synthetische term op ("Duitsland") die in geen
   * enkele bronmodule bestaat, waardoor alles wat de opgeslagen waarde later moet matchen
   * — filters, exports, backend-regels — een uitzondering nodig had. Nu bevat het model
   * altijd echte opties; "alles" is een bedieningsgemak, geen extra soort waarde.
   * Nogmaals kiezen haalt de hele groep weer weg.
   */
  selectAllInParent(parent: TagsMenuOption, event?: Event): void {
    event?.stopPropagation();
    this.field.formControl.markAsTouched();
    const children = parent.children ?? [];
    const childKeys = new Set(children.map(child => this.tagKey(child.value)));

    if (this.isParentFullySelected(parent)) {
      this.tags.update(current => current.filter(tag => !childKeys.has(this.tagKey(tag))));
    } else {
      // Alleen de ontbrekende kinderen erbij, zodat een al gekozen regio niet dubbel komt.
      const huidige = this.#selectedKeys();
      const ontbrekend = children.filter(child => !huidige.has(this.tagKey(child.value))).map(child => child.value);
      // maxTags: a bulk add that would exceed the cap refuses entirely — no partial selection.
      // Same refusal-once-at-the-cap rule as a single option; isBulkSelectCapped mirrors this
      // check to drive the control's own capped affordance.
      if (this.wouldExceedMaxTags(ontbrekend.length)) {
        return;
      }
      this.tags.update(current => [...current, ...ontbrekend]);
    }
    this.updateModel();

    if (!this.isMultiSelect()) this.menuTrigger?.closeMenu();
  }

  /** Alle kinderen van de parent gekozen — stuurt het vinkje op "Alles in {parent}". */
  isParentFullySelected(parent: TagsMenuOption): boolean {
    const children = parent.children ?? [];
    if (children.length === 0) {
      return false;
    }
    const selectedKeys = this.#selectedKeys();
    return children.every(child => selectedKeys.has(this.tagKey(child.value)));
  }

  isLoading = signal(false);
  loaded = signal(false);
  // Track if all options have been loaded (for lazy loading)
  private allOptionsLoaded = signal(false);
  private cleanup?: () => void;

  constructor() {
    super();
  }

  ngOnInit() {
    this.initializeOptionsLazy();
  }

  ngAfterViewInit() {
    requestAnimationFrame(() => {
      this.initializeDynamicOptions();
      this.setupValueChanges();

      this.field.formControl.statusChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
        requestAnimationFrame(() => {
          this.setDisabledState(this.isDisabled());
        });
      });
      this.setDisabledState(this.isDisabled());
    });

    // Follow the control instead of reading it once — and OUTSIDE the rAF above: in a
    // hidden/loading iframe (Storybook cold start) that animation frame may never fire,
    // which left the chips empty while the control did carry the value. Reactive follow
    // also means programmatic form writes reach the chips.
    this.field.formControl.valueChanges
      .pipe(startWith(this.field.formControl.value), takeUntil(this.destroy$))
      .subscribe(value => {
        const next = Array.isArray(value) ? value : [];
        if (next !== this.tags()) {
          this.tags.set(next);
        }
      });
  }

  /**
   * Lazy loading strategy for options:
   * - No value (empty array): Don't fetch anything, wait for interaction
   * - Has values (array with items): Fetch only those items to validate they exist
   * - Full options are loaded when user starts typing
   */
  private initializeOptionsLazy(): void {
    // Static options (no module-linked optionsArray, plain props.options list): the values are
    // self-describing strings, so there is nothing to fetch — just seed the dropdown.
    if (this.isStaticOptions()) {
      this.setStaticOptions();
      this.isLoading.set(false);
      this.loaded.set(true);
      return;
    }

    // Module-backed buttons need the complete option list after linkedData is initialized.
    // Fetching selected values here can race that full load and replace it with a partial list.
    if (this.props.tagsDisplayMode === 'buttons') {
      this.isLoading.set(false);
      this.loaded.set(true);
      return;
    }

    const currentValues = this.field.formControl?.value;

    if (!currentValues || !Array.isArray(currentValues) || currentValues.length === 0) {
      // No initial values - don't load anything, wait for user interaction
      this.isLoading.set(false);
      this.loaded.set(true);
      return;
    }

    // Has initial values - fetch only those to validate they exist
    this.isLoading.set(true);
    this.dynamicOptionsService.fetchMultipleOptionsByValues(this.field, currentValues).subscribe({
      next: options => {
        this.#publishOptions(options);
        this.isLoading.set(false);
        this.loaded.set(true);
      },
      error: () => {
        this.isLoading.set(false);
        this.loaded.set(true);
      },
    });
  }

  /** Term of the fetch that filled `availableOptions` — '' after an unfiltered load. */
  #lastFetchedTerm = '';

  /**
   * Load all options - called when user starts typing
   */
  private loadAllOptions(searchTerm = ''): void {
    // Static options: filter the provided list client-side instead of fetching from a module.
    if (this.isStaticOptions()) {
      this.setStaticOptions(searchTerm);
      return;
    }

    // No option source at all (neither props.options nor an optionsArray): there is nothing to
    // load. Both request paths degenerate on an empty source list — forkJoin([]) and merge()
    // over zero observables complete without ever emitting — which would leave isLoading stuck
    // on true and the overlay spinning.
    if (this.#optionSources.length === 0) {
      return;
    }

    // Skip only when the list already reflects an UNFILTERED load: after search-then-close,
    // allOptionsLoaded is still true but availableOptions holds the filtered results — a
    // reopened overlay would otherwise show only the previous search's hits.
    if (this.allOptionsLoaded() && !searchTerm && this.#lastFetchedTerm === '') {
      return;
    }

    // Bugfix #20951, reinstated after the main merge dropped it: fetchOptionsForValue() treats
    // EVERY optionsArray entry as `optionsType: 'module'`. createLinkedData ignores optionsType
    // and only resolves `moduleCode` (dynamic-options.service.ts:108-114), and
    // mapEntriesToOptions branches solely on `moduleValueType` with no fallback
    // (dynamic-options.service.ts:600-671) — a taxonomy source has none, so the field silently
    // ended up with zero options, both on open and on every keystroke. initializeOptions() does
    // branch on optionsType and is the path select/radio/checkbox already use for a full load.
    // Module-only sources deliberately KEEP fetchOptionsForValue: it carries the request cache,
    // the offset/limit pagination and the checkAvailability planning logic that
    // initializeOptions lacks, so linked pickers (RDW vehicles, planning) are untouched.
    // A manual list never reaches this point — isStaticOptions() handled it above.
    //
    // Scope: the condition is "all sources are module", not "no source is module". An
    // optionsArray that carries a module source alongside another source therefore goes through
    // initializeOptions in its entirety — it is the only path that knows every source type.
    // Module-only fields keep the rich path, so the linked pickers (RDW vehicles, planning)
    // follow exactly the same route as before.
    const moduleFetch = this.#hasOnlyModuleOptionSources();
    const request$ = moduleFetch
      ? this.dynamicOptionsService.fetchOptionsForValue(
          this.dynamicFilterValue(),
          this.linkedData(),
          searchTerm,
          this.field,
          0,
          this.#optionFetchLimit()
        )
      : this.dynamicOptionsService.initializeOptions(this.field, searchTerm, this.#optionFetchLimit());

    this.isLoading.set(true);
    request$.subscribe({
      next: options => {
        // Client-side generated sources (taxonomy) never get their search term into a backend
        // filter, so their labels are filtered here. Module sources are filtered server-side by
        // buildFilters(); re-filtering those on label would drop hits that matched on another
        // field. The filtering happens INSIDE #publishOptions, after the fold — see there.
        this.#publishOptions(options, !moduleFetch && this.#hasOnlyLocalOptionSources() ? searchTerm : '');
        this.#lastFetchedTerm = searchTerm;
        if (!searchTerm) {
          this.allOptionsLoaded.set(true);
        }
        this.isLoading.set(false);
      },
      error: () => {
        this.isLoading.set(false);
      },
      // initializeOptions builds one observable per RECOGNISED optionsType and merges them, so a
      // source it has no branch for yields merge() over zero observables — which completes
      // without ever emitting and would leave the spinner running. No such optionsType is
      // reachable for a tags field today ('userroles' is only ever stamped onto
      // custom-userroles-multicheckbox, form-generate.util.mjs:506-508), so this is defence
      // against a future one rather than a live bug. Idempotent next to next(), no-op on the
      // module path.
      complete: () => {
        this.isLoading.set(false);
      },
    });
  }

  /**
   * This field's option sources as configured in the form builder. Read from the field config
   * and NOT from `linkedData()`: createLinkedData (dynamic-options.service.ts:108-114) flattens
   * every entry into a module lookup and discards `optionsType` — exactly the distinction the
   * helpers below need.
   */
  get #optionSources(): Array<{ optionsType?: string }> {
    return this.field.props?.optionsArray ?? [];
  }

  /**
   * Only module-linked sources — including legacy items that predate `optionsType` and were
   * therefore always handled as module sources.
   */
  #hasOnlyModuleOptionSources(): boolean {
    const sources = this.#optionSources;
    return sources.length > 0 && sources.every(source => source.optionsType == null || source.optionsType === 'module');
  }

  /**
   * True when NO source is fetched from a module: initializeOptions generates the whole list
   * client-side, so the search term reached no backend filter and the labels have to be
   * filtered here. Today that means taxonomy-only — a manual list is caught earlier by
   * isStaticOptions() and never gets here.
   *
   * A legacy source without `optionsType` counts as a module here too, making this the exact
   * complement of #hasOnlyModuleOptionSources. Without that `!= null`, a mixed
   * [legacy, taxonomy] list would re-filter its server-filtered module hits on label and
   * silently drop them.
   */
  #hasOnlyLocalOptionSources(): boolean {
    const sources = this.#optionSources;
    return sources.length > 0 && sources.every(source => source.optionsType != null && source.optionsType !== 'module');
  }

  private initializeDynamicOptions(): void {
    // A manual list carries an optionsArray too, and createLinkedData turns any entry into a
    // module lookup — that fetch would resolve to nothing and wipe the seeded options.
    if (this.isStaticOptions()) {
      return;
    }

    const { linkedData, cleanup } = this.dynamicOptionsService.initializeDynamicOptions(
      {
        field: this.field,
        lazyLoad: true, // Skip initial fetch, load options when user types
        onOptionsUpdate: options => {
          this.#publishOptions(options);
          this.isLoading.set(false);
          this.loaded.set(true);
          this.setDisabledState(this.isDisabled());
        },
        onFilterValueUpdate: value => {
          this.dynamicFilterValue.set(value);
        },
      },
      this.destroy$
    );

    this.linkedData.set(linkedData);
    this.cleanup = cleanup;

    // Menu and inline modes fetch on interaction. Buttons have no such interaction point:
    // their options are the field, so load them once linkedData is available.
    if (this.props.tagsDisplayMode === 'buttons') {
      this.loadAllOptions();
    }
  }

  private setupValueChanges(): void {
    // Handle input changes for filtering. In de geneste subview filteren we de statische
    // kinderen client-side (menuSearchTerm); een fetch zou de hoofdlijst overschrijven.
    this.tagsInput.valueChanges
      .pipe(takeUntil(this.destroy$), debounceTime(300), distinctUntilChanged())
      .subscribe(value => {
        this.menuSearchTerm.set(value ?? '');
        if (!this.menuParent()) {
          this.loadAllOptions(value || '');
        }
      });
  }

  /**
   * Called when the input field receives focus - loads all options
   */
  onInputFocus(): void {
    this.loadAllOptions();
  }

  /** Called when the "Toevoegen" overlay opens (menu display mode) — mirrors onInputFocus(). */
  onMenuOpened(): void {
    this.loadAllOptions();
  }

  /** Reset de geneste weergave en zoekterm zodra de overlay sluit. */
  onMenuClosed(): void {
    this.menuParent.set(null);
    this.tagsInput.setValue(null, { emitEvent: false });
    this.menuSearchTerm.set('');
  }

  /**
   * `maxTags` reached: further selection is refused, deselecting stays possible. (#20952)
   *
   * Gated on the CONFIGURED display mode (`tagsDisplayMode === 'buttons'`), not the effective
   * render mode (`isButtonsMode()`). This mirrors the generator's emit condition — it only
   * writes `maxTags` for a buttons-configured field — so a buttons-configured field keeps its
   * cap even when it falls back to the nested mat-menu overlay (two-level options make
   * `isButtonsMode()` stand down, see that computed's own JSDoc). Menu-configured fields —
   * including legacy/handwritten configs that carry a stray `maxTags` — stay uncapped: outside
   * this cap's own tooltip affordance the overlay has nothing explaining a refusal, so blocking
   * a field that was never meant to have one would read as a silently broken control.
   */
  isMaxTagsReached = computed(() => this.wouldExceedMaxTags(1));

  /** Optie die nu niet te kiezen is: niet-geselecteerd terwijl het maximum bereikt is. */
  isOptionCapped(option: TagsMenuOption): boolean {
    return this.isInteractive() && this.isMaxTagsReached() && !this.isValueSelected(option.value);
  }

  /**
   * "Alles in {parent}" would exceed the max — same capped affordance as an individual option
   * button (not-allowed cursor + tooltip). Deselecting (parent already fully selected) is
   * always allowed, so a fully-selected parent never carries this class.
   */
  isBulkSelectCapped(parent: TagsMenuOption): boolean {
    if (!this.isInteractive() || this.isParentFullySelected(parent)) {
      return false;
    }
    const selectedKeys = this.#selectedKeys();
    const missing = (parent.children ?? []).filter(child => !selectedKeys.has(this.tagKey(child.value))).length;
    return this.wouldExceedMaxTags(missing);
  }

  /**
   * Would adding `additionalCount` more values push the selection past the configured max?
   * Scoped to buttons-CONFIGURED fields only — see `isMaxTagsReached`'s JSDoc for why. Backs
   * both the single-option gate (`additionalCount: 1`) and the bulk "Alles in {parent}" refusal,
   * which can add more than one value at once.
   */
  private wouldExceedMaxTags(additionalCount: number): boolean {
    if (this.props.tagsDisplayMode !== 'buttons') {
      return false;
    }
    const max = this.#maxTags();
    return max > 0 && this.tags().length + additionalCount > max;
  }

  /** Normalized `maxTags` prop. 0 = no limit. */
  #maxTags(): number {
    const max = Number(this.props['maxTags']);
    return Number.isFinite(max) && max > 0 ? max : 0;
  }

  /**
   * Waarom een geblokkeerde optie de klik weigert. Zonder deze uitleg deed de klik stil niets,
   * wat als een kapotte knop leest — de not-allowed-cursor in _chips.scss is het tweede signaal.
   *
   * A plain method, not a computed: `props` is a plain object, not a signal, so a computed built
   * from it has zero tracked dependencies — it evaluates once and then freezes on that first
   * value forever, going stale the moment `maxTags` changes on the same instance. A method reads
   * `props['maxTags']` fresh on every call; the capped-gated `tippyTooltipDisabled` bindings in
   * the template already keep the call frequency low.
   */
  maxTagsTooltip(): string {
    const max = this.#maxTags();
    return max > 0
      ? `Maximaal ${max} ${max === 1 ? 'keuze' : 'keuzes'} — verwijder er eerst een om een andere te kiezen.`
      : '';
  }

  /**
   * Kies (of in multi-select/buttons-weergave: toggle) een optie.
   *
   * Sinds "Alles in {parent}" de kinderen zelf aanvinkt, is een kind gewoon een kind: er is
   * geen parent-waarde meer die het zou kunnen overlappen. Een kind uitvinken na "alles"
   * laat dus simpelweg de rest staan, en "Alles in {parent}" verliest zijn vinkje.
   */
  selectMenuOption(option: TagsMenuOption, event?: Event): void {
    // Panel-klikken sluiten deze mat-menu-setup; alleen single-select mag sluiten (expliciet).
    event?.stopPropagation();
    // Read-only/disabled velden muteren nooit. De knop is in de DOM al disabled, maar de methode
    // is ook programmatisch bereikbaar — een alleen-lezen veld dat alsnog schrijft is een bug.
    if (!this.isInteractive()) {
      return;
    }
    this.field.formControl.markAsTouched();
    const togglesSelection = this.isMultiSelect() || this.isButtonsMode();
    if (togglesSelection && this.isValueSelected(option.value)) {
      this.remove(this.tags().find(tag => this.tagKey(tag) === this.tagKey(option.value)));
    } else {
      // maxTags: verder kiezen wordt geweigerd, deselecteren blijft altijd mogelijk. De reden
      // staat in de tooltip op de knop, dus de klik hoeft hier niets te melden.
      if (this.isMaxTagsReached()) {
        return;
      }
      this.addTagIfAbsent(option.value);
    }
    this.tagsInput.setValue(null, { emitEvent: false });
    this.menuSearchTerm.set('');
    if (!togglesSelection) this.menuTrigger?.closeMenu();
  }

  selected(event: { option: { value: any } }): void {
    this.field.formControl.markAsTouched();
    this.addTagIfAbsent(event.option.value);
    this.input.nativeElement.value = '';
    this.tagsInput.setValue(null);
  }

  add(event: MatChipInputEvent): void {
    this.field.formControl.markAsTouched();
    const value = (event.value || '').trim();

    if (value) {
      if (this.props.requireSelection === false) {
        this.addTagIfAbsent(value);
      } else {
        const matchingOption = this.availableOptions().find(
          option => option.label === value || option.label === value?.['selectedInput']
        );
        if (matchingOption) {
          this.addTagIfAbsent(matchingOption.value);
        }
      }
    }

    event.chipInput!.clear();
  }

  /**
   * Stable dedup key for a tag value — module-linked options carry `{ id, selectedInput }`
   * objects (compare by id), free-text tags are plain strings/primitives (compare by value).
   */
  private tagKey(tag: any): string {
    if (tag != null && typeof tag === 'object') {
      return String(tag.id ?? tag.value ?? tag.selectedInput ?? JSON.stringify(tag));
    }
    return String(tag);
  }

  /**
   * Push a tag onto the model, skipping it when an equal value is already selected.
   * Bugfix #20806: `add()`/`selected()` used to push unconditionally, so the same option
   * could end up twice — once from a free-text match and once from autocomplete selection.
   */
  private addTagIfAbsent(value: any): void {
    const key = this.tagKey(value);
    if (this.tags().some(tag => this.tagKey(tag) === key)) {
      return;
    }
    this.tags.update(currentTags => [...currentTags, value]);
    this.updateModel();
  }

  remove(tag: any): void {
    // Match by tagKey, not reference: stored tags are deserialized copies (valueChanges sync),
    // so an option-value passed in (e.g. selectAllInParent's deselect) never matches indexOf
    // and the removal would be a silent no-op.
    const key = this.tagKey(tag);
    this.tags.update(currentTags => {
      const index = currentTags.findIndex(currentTag => this.tagKey(currentTag) === key);
      if (index >= 0) {
        const newTags = cloneDeep(currentTags);
        newTags.splice(index, 1);
        return newTags;
      }
      return currentTags;
    });
    this.updateModel();
  }

  edit(tag: any, event: MatChipEditedEvent): void {
    const value = event.value.trim();

    if (!value) {
      this.remove(tag);
      return;
    }

    this.tags.update(currentTags => {
      const index = currentTags.indexOf(tag);
      if (index >= 0) {
        return currentTags.map((currentTag, i) => (index === i ? value : currentTag));
      }
      return currentTags;
    });
    this.updateModel();
  }

  private updateModel(): void {
    this.formControl.setValue(this.tags());
    // Only user interactions (add/remove/select/edit) reach this method — never the
    // valueChanges→tags sync — so the save button's dirty gate must see the change.
    this.formControl.markAsDirty();
  }

  /**
   * True for a plain static-options field: no module-linked `optionsArray`, but a direct
   * `props.options` list (array or observable). Module-linked tags keep their dynamic path.
   */
  private isStaticOptions(): boolean {
    // A manual list is static too — it needs no fetch, only client-side filtering.
    if (this.#manualOptions()) {
      return true;
    }
    return !this.field.props?.optionsArray?.length && this.field.props?.options != null;
  }

  /** Seed/filter `availableOptions` from the static option list (client-side search). */
  private setStaticOptions(searchTerm = ''): void {
    const term = searchTerm.toLowerCase();
    const apply = (raw: Array<{ label: string; value: unknown }>) => {
      // Fold BEFORE filtering: the search term must match the labels the user actually
      // sees, which after nesting are the group names, not the individual regions.
      const all = this.#prepareOptions(raw as Array<{ label: string; value: any }>);
      // Index the FULL list — the filtered subset below may exclude already-selected values.
      this.#indexOptionLabels(all as TagsMenuOption[]);
      this.availableOptions.set(term ? all.filter(option => this.#matchesTerm(option as TagsMenuOption, term)) : all);
    };

    // A manual list wins over props.options: it is the field's configured source.
    const manual = this.#manualOptions();
    if (manual) {
      apply(manual);
      return;
    }

    const options = this.field.props?.options;
    if (isObservable(options)) {
      options.pipe(take(1), takeUntil(this.destroy$)).subscribe(all => apply(all ?? []));
    } else {
      apply(Array.isArray(options) ? options : []);
    }
  }

  /**
   * `tagsNestByGroup` folds the flat `group` label that `optionsArray.groupByField`
   * produces into the `children[]` drilldown (land → regio's). Opt-in on purpose: a
   * module-linked tags field with grouping already configured renders a flat list today,
   * and must keep doing so unless the config asks for nesting.
   *
   * Applied to both option sources, so the rule is the same wherever the list comes from.
   * Options that already declare `children` are untouched — they carry no `group`.
   */
  #prepareOptions(options: Array<{ label: string; value: any }>): Array<{ label: string; value: any }> {
    return this.props.tagsNestByGroup === true ? nestOptionsByGroup(options) : options;
  }

  /**
   * How many options to fetch per load. A nested field needs the WHOLE set, not the
   * default first page: grouping over a partial page hides entire groups and
   * under-reports the rest ("Duitsland (3)" when the module holds 16 regions), which
   * reads as missing data rather than as paging. Flat fields keep the default page —
   * they show what they fetched and searching narrows it server-side.
   */
  #optionFetchLimit(): number {
    return this.props.tagsNestByGroup === true || this.props.tagsDisplayMode === 'buttons' ? 1000 : 25;
  }

  /**
   * Client-side search match for the static list. A parent also matches on its CHILD
   * labels — typing "Beieren" has to keep "Duitsland" in view, otherwise the only row
   * that leads to the match disappears. Flat options are unaffected: no children, so
   * this is the plain label test they always had.
   */
  #matchesTerm(option: TagsMenuOption, term: string): boolean {
    if ((option.label ?? '').toLowerCase().includes(term)) {
      return true;
    }
    return (option.children ?? []).some(child => (child.label ?? '').toLowerCase().includes(term));
  }

  /**
   * Replace the option list AND remember every label seen (see `#labelIndex`).
   *
   * `clientFilterTerm` is only passed for client-side generated sources (taxonomy), whose search
   * term never reached a backend filter. Fold BEFORE filtering — the same order setStaticOptions
   * uses — because after nesting the user sees GROUP labels: filtering the flat list first would
   * remove "Duitsland", the only row that leads to a match on "Beieren". The FULL folded list is
   * indexed, since the filtered subset may omit already-selected values whose chip still needs a
   * label.
   */
  #publishOptions(options: Array<{ label: string; value: any }>, clientFilterTerm = ''): void {
    const prepared = this.#prepareOptions(options);
    this.#indexOptionLabels(prepared as TagsMenuOption[]);
    // No trim, deliberately: setStaticOptions lowercases the raw term too, and the two
    // client-side paths must narrow identically for the same keystrokes.
    const term = clientFilterTerm.toLowerCase();
    this.availableOptions.set(
      term ? prepared.filter(option => this.#matchesTerm(option as TagsMenuOption, term)) : prepared
    );
  }

  /**
   * Composed "Parent: Kind" labels, keyed by child. Kept separate from `#labelIndex`
   * so `tagLabel` can tell a nested child apart from a plain option — a module-linked
   * child carries an object value, and without that distinction it would fall into the
   * object branch and render bare ("Beieren" instead of "Duitsland: Beieren").
   */
  readonly #composedLabelIndex = new Map<string, string>();

  #indexOptionLabels(options: TagsMenuOption[]): void {
    for (const option of options) {
      this.#labelIndex.set(this.tagKey(option.value), option.label);
      for (const child of option.children ?? []) {
        // Same composed form tagLabel renders for a nested child ("Duitsland: Beieren").
        const composed = `${option.label}: ${child.label}`;
        this.#labelIndex.set(this.tagKey(child.value), composed);
        this.#composedLabelIndex.set(this.tagKey(child.value), composed);
      }
    }
  }

  private setDisabledState(disabled: boolean): void {
    if (disabled) {
      this.tagsInput.disable({ emitEvent: false });
    } else {
      this.tagsInput.enable({ emitEvent: false });
    }
  }

  isDisabled(): boolean {
    return this.field.props.disabled || this.form.disabled;
  }

  isReadOnly(): boolean {
    return this.field.props.readonly === true;
  }

  /** Add/remove affordances render only for someone who may actually change the value. */
  isInteractive(): boolean {
    return !this.isDisabled() && !this.isReadOnly();
  }

  /**
   * Button text names the subject (Figma "Add feature"): "Functies toevoegen" from the
   * field label, an explicit override, or the bare fallback.
   *
   * `addText` is the configurable route — it is the prop the form builder already stores
   * and the generator already emits, whereas `addButtonLabel` was only ever readable from
   * code. Needed as soon as label and button diverge: "Locaties (regionaal &
   * internationaal)" as the label, "Locatie toevoegen" on the button.
   */
  addLabel(): string {
    if (this.props.addButtonLabel) return this.props.addButtonLabel;
    if (this.props.addText) return this.props.addText;
    const label = (this.props.label ?? '').trim();
    return label ? `${label} toevoegen` : 'Toevoegen';
  }

  /**
   * Display label for a tag value. Module-linked tags carry `{ id, selectedInput }` objects;
   * option-picked tags store the raw option VALUE (e.g. 'adr'), so render the matching
   * option's label ('ADR') instead of the value. Free-text tags fall through unchanged.
   */
  tagLabel(tag: any): string {
    // Nested child first — also for module-linked (object) values, which would otherwise
    // return bare from the object branch below and lose the "Duitsland: Beieren" form.
    const composed = this.#composedLabelIndex.get(this.tagKey(tag));
    if (composed != null) {
      return composed;
    }
    if (tag != null && typeof tag === 'object') {
      return tag.selectedInput ?? tag.label ?? String(tag.id ?? '');
    }
    const key = this.tagKey(tag);
    for (const option of this.availableOptions() as TagsMenuOption[]) {
      if (this.tagKey(option.value) === key) return option.label;
      // Kind van een geneste parent → Figma-chipvorm "Germany: Bavaria".
      const child = option.children?.find(c => this.tagKey(c.value) === key);
      if (child) return `${option.label}: ${child.label}`;
    }
    // Niet (meer) in de actuele lijst — een zoek-fetch vervangt die — maar wél ooit gezien:
    // het groeiende woordenboek houdt het label van bestaande chips overeind (B5).
    const indexed = this.#labelIndex.get(key);
    if (indexed != null) {
      return indexed;
    }
    return String(tag);
  }

  ngOnDestroy() {
    this.cleanup?.();
  }
}
